import { useEffect } from "react";

// Public canonical origin. Kept as a constant so every page emits the same
// canonical host regardless of preview URL, subdomain, or custom domain.
const CANONICAL_BASE = "https://gateandgather.co.nz";

const DEFAULT_TITLE = "Gate + Gather — Simple selling for independent growers";
const DEFAULT_DESCRIPTION =
  "Gate + Gather gives independent growers a simple online storefront. Show what's available, take orders and keep track of what's sold — without running a complicated ecommerce store.";

// Direct DOM mutation. We tried react-helmet-async 2.x on React 19 and it
// only synced <title> reliably — the meta/link tags were dropped in strict
// mode. Owning the effect ourselves is a dozen lines and behaves the same
// on every route.
function upsertMeta(key, content, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? "property" : "name";
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Upsert one or more <script type="application/ld+json"> blocks in <head>.
 *
 * Each entry is stamped with `data-seo-jsonld="<id>"` so navigating to
 * another page can cleanly remove the previous page's blocks (e.g. leaving
 * a Product Gate for the Landing page).
 */
function syncJsonLd(entries) {
  // Wipe any previous SEO-owned JSON-LD blocks — we redraw from scratch on
  // every route so stale schemas from a previous page never linger.
  document.head
    .querySelectorAll('script[type="application/ld+json"][data-seo-jsonld]')
    .forEach((n) => n.remove());
  if (!entries || entries.length === 0) return;
  entries.forEach((data, i) => {
    const el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-jsonld", String(i));
    el.textContent = JSON.stringify(data);
    document.head.appendChild(el);
  });
}

/**
 * Per-route <head> tags.
 *
 * Every public page passes a unique {title, description, path}. Auth,
 * dashboard, admin and checkout screens pass {noindex:true} so search
 * engines drop them from the index — that's the standard fix for the
 * "duplicate content / low word count" SEO flags on login/signup/forgot.
 *
 * Optional {jsonLd}: an array of schema.org JSON-LD objects to inject as
 * <script type="application/ld+json"> in <head>. Pass a single object and
 * it will be wrapped for you.
 */
export default function Seo({
  title,
  description,
  path,
  noindex = false,
  image = "/apple-touch-icon.svg",
  jsonLd = null,
}) {
  // If the caller already includes "Gate + Gather" in the title string, use it
  // verbatim — this lets specific SEO briefs supply the exact wording (including
  // the `|` separator preferred by search engines) without our template
  // stamping a second brand name on the end. Otherwise, append the template.
  const fullTitle = title
    ? (title.includes("Gate + Gather") ? title : `${title} · Gate + Gather`)
    : DEFAULT_TITLE;
  const desc = description || DEFAULT_DESCRIPTION;
  const canonical = `${CANONICAL_BASE}${path || "/"}`;
  const ogImage = image.startsWith("http") ? image : `${CANONICAL_BASE}${image}`;
  const robots = noindex ? "noindex, follow" : "index, follow";
  // Stable key for the JSON-LD effect so the list identity doesn't change on
  // every render (which would thrash the <head> nodes needlessly).
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("description", desc);
    upsertMeta("robots", robots);
    upsertLink("canonical", canonical);
    upsertMeta("og:type", "website", true);
    upsertMeta("og:site_name", "Gate + Gather", true);
    upsertMeta("og:title", fullTitle, true);
    upsertMeta("og:description", desc, true);
    upsertMeta("og:url", canonical, true);
    upsertMeta("og:image", ogImage, true);
    upsertMeta("twitter:card", "summary_large_image");
    upsertMeta("twitter:title", fullTitle);
    upsertMeta("twitter:description", desc);
    upsertMeta("twitter:image", ogImage);
    const entries = jsonLd == null ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    syncJsonLd(entries);
  }, [fullTitle, desc, canonical, ogImage, robots, jsonLdKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
